import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  PlusIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../adapters/useAppAuth';
import { PatientsProvider, usePatients } from '../contexts/PatientsContext';
import { useUI, UIProvider } from '../contexts/UIContext';
import { RecordingProvider } from '../contexts/RecordingContext';
import { NotificationsProvider } from '../contexts/NotificationsContext';
import PatientNavPanel from '../components/PatientNavPanel';
import HeaderRecordingDropdown from '../components/HeaderRecordingDropdown';
import HeaderNotificationsBell from '../components/HeaderNotificationsBell';
import { GlobalPatientSearch } from '../components/GlobalPatientSearch';
import CreatePatientModal from '../components/patients/CreatePatientModal';
import { Patient } from '../types';
import LogoMain from '../../assets/Logo_main.svg';
import LogoSmall from '../../assets/Logo_small.svg';

function classNames(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

interface PrimarySidebarProps {
  onOpenCreate: () => void;
}

const PrimarySidebar = ({ onOpenCreate }: PrimarySidebarProps) => {
  const { user, logout } = useAuth();
  const [profileExpanded, setProfileExpanded] = React.useState(false);
  const navigate = useNavigate();
  const { primaryOpen, togglePrimary } = useUI();

  const f = (user?.first_name || '').trim();
  const l = (user?.last_name || '').trim();
  const initials = ((f ? f[0] : '') + (l ? l[0] : '')).toUpperCase();

  return (
    <div
      className={classNames(
        'bg-white border-r border-brand-navy/15 shadow-lg flex flex-col h-screen fixed top-0 left-0 z-50 transition-[width] duration-200 ease-in-out',
        primaryOpen ? 'w-64' : 'w-16',
      )}
    >
      {primaryOpen ? (
        <div className='h-16 flex items-center px-3 border-b border-brand-navy/15 gap-2'>
          <button
            onClick={togglePrimary}
            className='flex items-center gap-2 shrink-0 focus:outline-none rounded'
            aria-label='Collapse sidebar'
            aria-expanded={true}
          >
            <img src={LogoMain} alt='Therapix.ai' className='h-8 w-auto select-none' draggable={false} />
          </button>
          <div className='flex-1' />
          <button
            onClick={togglePrimary}
            className='text-gray-500 hover:text-gray-700 p-2 rounded focus:outline-none transition-colors'
            aria-label='Collapse sidebar'
          >
            <ChevronLeftIcon className='h-5 w-5' />
          </button>
        </div>
      ) : (
        <div className='h-16 flex items-center justify-center px-2 border-b border-brand-navy/15'>
          <button
            onClick={togglePrimary}
            className='group flex items-center justify-center rounded focus:outline-none transition-colors w-full h-16 p-2'
            aria-label='Expand sidebar'
            aria-expanded={false}
          >
            <img src={LogoSmall} alt='Therapix.ai' className='h-8 w-auto select-none' draggable={false} />
          </button>
        </div>
      )}

      <nav className='flex-1 py-4 px-2 flex flex-col min-h-0' aria-label='Primary'>
        {primaryOpen && <PatientNavPanel />}
      </nav>

      <div className='border-t p-2 text-sm'>
        {primaryOpen && (
          <div className='mb-2 px-1'>
            <button
              onClick={onOpenCreate}
              className='w-full flex items-center justify-center gap-1 text-xs py-2 rounded bg-brand-navy text-white hover:bg-brand-navy/90 focus:outline-none focus:ring-2 focus:ring-brand-navy/40'
            >
              <PlusIcon className='h-4 w-4' /> Add Patient
            </button>
          </div>
        )}
        <button
          type='button'
          onClick={() => {
            if (!primaryOpen) { navigate('/profile'); return; }
            setProfileExpanded(v => !v);
          }}
          aria-expanded={primaryOpen ? profileExpanded : undefined}
          className='group w-full flex items-center gap-3 rounded px-2 py-2 text-left focus:outline-none transition-colors hover:bg-brand-navy/5'
          aria-label={primaryOpen ? 'Profile menu' : 'Open profile'}
        >
          {initials ? (
            <span className='h-8 w-8 rounded-full bg-brand-navy text-white flex items-center justify-center text-sm font-semibold shadow-sm select-none' aria-hidden='true'>
              {initials}
            </span>
          ) : (
            <UserCircleIcon className='h-8 w-8 text-gray-400' aria-hidden='true' />
          )}
          {primaryOpen && (
            <>
              <div className='min-w-0 flex-1 flex flex-col'>
                <span className='font-medium text-gray-700 leading-5 truncate'>
                  {(`${f} ${l}`.trim()) || 'Profile'}
                </span>
                {user?.email && (
                  <span className='text-xs text-gray-400 truncate'>{user.email}</span>
                )}
              </div>
              <svg
                className={classNames('h-4 w-4 text-gray-400 transition-transform', profileExpanded ? 'rotate-180' : 'rotate-0')}
                viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'
              >
                <path fillRule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z' clipRule='evenodd' />
              </svg>
            </>
          )}
        </button>
        <div
          className={classNames(
            'overflow-hidden transition-all duration-200 ease-in-out flex flex-col',
            primaryOpen && profileExpanded ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0',
          )}
          aria-hidden={!profileExpanded}
        >
          <div className='mt-1 space-y-1 px-2 pb-1'>
            <button
              type='button'
              tabIndex={profileExpanded ? 0 : -1}
              onClick={() => { navigate('/profile'); setProfileExpanded(false); }}
              className='w-full flex items-center gap-2 text-xs px-3 py-2 rounded-md text-gray-700 hover:bg-brand-navy/10 hover:text-brand-navy transition-colors'
            >
              <Cog6ToothIcon className='h-4 w-4' />
              <span>Settings</span>
            </button>
            <button
              type='button'
              tabIndex={profileExpanded ? 0 : -1}
              onClick={() => { logout(); setProfileExpanded(false); }}
              className='w-full flex items-center gap-2 text-xs px-3 py-2 rounded-md text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors'
            >
              <ArrowRightOnRectangleIcon className='h-4 w-4' />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SidebarLayout = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { primaryOpen } = useUI();
  const { addPatient } = usePatients();
  const [showCreate, setShowCreate] = React.useState(false);
  const leftOffset = primaryOpen ? 'ml-64' : 'ml-16';
  const headerLeft = primaryOpen ? '16rem' : '4rem';

  return (
    <div className='min-h-screen bg-gray-100'>
      <PrimarySidebar onOpenCreate={() => setShowCreate(true)} />
      <div
        className='fixed top-0 right-0 h-16 flex items-center gap-6 px-4 bg-white border-b border-brand-navy/15 shadow-sm z-30 transition-all'
        style={{ left: headerLeft }}
      >
        <button
          onClick={() => navigate('/app')}
          className='flex items-center gap-2 text-brand-navy hover:text-brand-navy/80 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-navy/40 rounded p-2'
          aria-label='Go to Dashboard'
        >
          <HomeIcon className='h-5 w-5' />
          <span className='text-sm font-medium'>Dashboard</span>
        </button>
        <GlobalPatientSearch />
        <div className='flex-1' />
        <HeaderRecordingDropdown />
        <HeaderNotificationsBell />
      </div>
      <main className={classNames('transition-all relative', leftOffset, 'pt-16')}>
        <div className='p-6'>{children}</div>
      </main>
      <CreatePatientModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(p: Patient) => {
          addPatient(p);
          navigate(`/patients/${p.patient_id}`);
        }}
      />
    </div>
  );
};

export const AppLayoutProviders = ({ children }: { children: React.ReactNode }) => (
  <UIProvider>
    <PatientsProvider>
      <RecordingProvider>
        <NotificationsProvider>{children}</NotificationsProvider>
      </RecordingProvider>
    </PatientsProvider>
  </UIProvider>
);

export { usePatients };
